import React, { useState, useEffect } from 'react';
import { Share2, Users, Lock, Shield, Eye, EyeOff, Plus, X, Check, User, Edit, Trash2 } from 'lucide-react';
import { Card } from '../common/Card';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';

interface Contact {
  id: string;
  name: string;
  email: string;
  is_deputy: boolean;
  permissions: string[];
}

interface SharedItem {
  id: string;
  type: 'document' | 'password' | 'account' | 'section';
  name: string;
  sharedWith: string[];
}

interface SharingWidgetProps {
  className?: string;
}

export function SharingWidget({ className = '' }: SharingWidgetProps) {
  const { user } = useAuth();
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [sharedItems, setSharedItems] = useState<SharedItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [selectedItem, setSelectedItem] = useState<string | null>(null);
  const [selectedContacts, setSelectedContacts] = useState<string[]>([]);
  const [showAddModal, setShowAddModal] = useState(false);
  const [newContact, setNewContact] = useState({ name: '', email: '', role: 'viewer' });
  const [showSettings, setShowSettings] = useState(false);
  
  // Sections that can be shared
  const sections = [
    { id: 'digital', name: 'Digital Estate' },
    { id: 'financial', name: 'Financial Management' },
    { id: 'legal', name: 'Legal Documents' },
    { id: 'health', name: 'Health Information' }
  ];
  
  // Load contacts and shared items
  useEffect(() => {
    const loadData = async () => {
      if (!user) return;
      
      setLoading(true);
      try {
        // Load contacts
        const { data: contactsData, error: contactsError } = await supabase
          .from('contacts')
          .select('id, name, email, is_deputy, permissions')
          .eq('user_id', user.id);
          
        if (contactsError) throw contactsError;
        
        // Load digital assets with shared_with field
        const { data: assetsData, error: assetsError } = await supabase
          .from('digital_assets')
          .select('id, name, type, shared_with')
          .eq('user_id', user.id)
          .not('shared_with', 'eq', '{}');
          
        if (assetsError) throw assetsError;
        
        // Process the data
        setContacts(contactsData || []);
        
        const processedItems: SharedItem[] = (assetsData || []).map(asset => ({
          id: asset.id,
          type: asset.type === 'password' ? 'password' : 'document',
          name: asset.name,
          sharedWith: asset.shared_with || []
        }));
        
        // Add mock section sharing for demonstration
        const mockSectionSharing: SharedItem[] = [
          {
            id: 'section_health',
            type: 'section',
            name: 'Health Information',
            sharedWith: ['contact1', 'contact2']
          }
        ];
        
        setSharedItems([...processedItems, ...mockSectionSharing]);
      } catch (error) {
        console.error('Error loading sharing data:', error);
      } finally {
        setLoading(false);
      }
    };
    
    loadData();
  }, [user]);
  
  const handleAddSharing = () => {
    if (!selectedItem || selectedContacts.length === 0) return;
    
    // Find the item to share
    const itemToShare = [...sharedItems].find(item => item.id === selectedItem);
    
    if (itemToShare) {
      // Update existing item
      setSharedItems(prev => 
        prev.map(item => 
          item.id === selectedItem 
            ? { ...item, sharedWith: [...selectedContacts] }
            : item
        )
      );
    } else {
      // Create new shared item
      const newItem: SharedItem = {
        id: selectedItem,
        type: selectedItem.startsWith('section_') ? 'section' : 'document',
        name: sections.find(s => `section_${s.id}` === selectedItem)?.name || selectedItem,
        sharedWith: [...selectedContacts]
      };
      
      setSharedItems(prev => [...prev, newItem]);
    }
    
    // Reset form
    setShowAddForm(false);
    setSelectedItem(null);
    setSelectedContacts([]);
  };
  
  const handleRemoveSharing = (itemId: string) => {
    setSharedItems(prev => prev.filter(item => item.id !== itemId));
  };
  
  const getContactName = (contactId: string) => {
    const contact = contacts.find(c => c.id === contactId);
    return contact ? contact.name : 'Unknown';
  };
  
  const toggleContact = (contactId: string) => {
    setSelectedContacts(prev => 
      prev.includes(contactId)
        ? prev.filter(id => id !== contactId)
        : [...prev, contactId]
    );
  };

  const handleAddContact = () => {
    // Implementation of adding a new contact
  };

  const handleEditContact = (contactId: string) => {
    // Implementation of editing a contact
  };

  const handleRemoveContact = (contactId: string) => {
    // Implementation of removing a contact
  };

  return (
    <Card className={`${className}`}>
      <div className="p-4">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Share2 size={18} className="text-idecide-accent" />
            <h3 className="font-semibold text-gray-900">Sharing Management</h3>
          </div>
          <button
            onClick={() => setShowAddModal(!showAddModal)}
            className="p-1 rounded-full hover:bg-idecide-primary/10 text-idecide-accent"
          >
            <Plus size={16} />
          </button>
        </div>
        
        {showAddModal && (
          <div className="mb-4 p-3 bg-idecide-primary/10 rounded-lg">
            <h4 className="font-medium text-gray-900 mb-2">Add New Contact</h4>
            <div className="space-y-2">
              <input
                type="text"
                placeholder="Contact name"
                value={newContact.name}
                onChange={(e) => setNewContact({ ...newContact, name: e.target.value })}
                className="w-full p-2 text-sm border rounded focus:ring-1 focus:ring-idecide-accent focus:border-idecide-accent"
              />
              <input
                type="email"
                placeholder="Email address"
                value={newContact.email}
                onChange={(e) => setNewContact({ ...newContact, email: e.target.value })}
                className="w-full p-2 text-sm border rounded focus:ring-1 focus:ring-idecide-accent focus:border-idecide-accent"
              />
              <div className="flex gap-2">
                <select
                  value={newContact.role}
                  onChange={(e) => setNewContact({ ...newContact, role: e.target.value })}
                  className="rounded text-idecide-accent focus:ring-idecide-accent"
                >
                  <option value="viewer">Viewer</option>
                  <option value="editor">Editor</option>
                  <option value="admin">Admin</option>
                </select>
                <span className="text-xs bg-idecide-primary/20 text-idecide-accent px-1.5 py-0.5 rounded-full">
                  {newContact.role}
                </span>
              </div>
              <div className="flex gap-2">
                <button
                  onClick={handleAddContact}
                  disabled={!newContact.name || !newContact.email}
                  className="px-3 py-1 text-xs bg-idecide-accent text-white rounded hover:bg-idecide-accent-hover disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Add Contact
                </button>
                <button
                  onClick={() => setShowAddModal(false)}
                  className="px-3 py-1 text-xs border border-gray-300 rounded hover:bg-gray-50"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}
        
        <div className="space-y-2">
          {contacts.map(contact => (
            <div
              key={contact.id}
              className="flex items-start justify-between p-2 rounded-lg hover:bg-idecide-light group"
            >
              <div className="flex items-start gap-3">
                <div className="mt-0.5 p-1.5 bg-idecide-primary/20 rounded-full">
                  <User size={16} className="text-idecide-accent" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-gray-900 truncate">{contact.name}</p>
                  <p className="text-sm text-gray-500 truncate">{contact.email}</p>
                  <div className="flex gap-1 mt-1">
                    {contact.permissions.map(permission => (
                      <span key={permission} className="text-xs bg-idecide-primary/20 text-idecide-accent px-1.5 py-0.5 rounded-full">
                        {permission}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                <button
                  onClick={() => handleEditContact(contact.id)}
                  className="p-1 text-gray-400 hover:text-idecide-accent"
                >
                  <Edit size={14} />
                </button>
                <button
                  onClick={() => handleRemoveContact(contact.id)}
                  className="p-1 text-gray-400 hover:text-idecide-error"
                >
                  <Trash2 size={14} />
                </button>
              </div>
            </div>
          ))}
        </div>
        
        <div className="mt-4 pt-3 border-t border-gray-200">
          <button
            onClick={() => setShowSettings(!showSettings)}
            className="mt-2 text-idecide-accent hover:text-idecide-accent-hover"
          >
            {showSettings ? 'Hide Settings' : 'Show Settings'}
          </button>
        </div>
      </div>
    </Card>
  );
}